// wcwb/frontend/src/views/Profile.js
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { S } from '../components/styles';

const DIETARY_OPTIONS = ['Vegetarian', 'Vegan', 'Gluten-free', 'Dairy-free', 'Pescatarian', 'Nut allergy', 'Halal', 'Kosher'];
const SKILL_OPTIONS   = ['Confident baker', 'Skilled with pasta', 'Creative with spice', 'Great with desserts', 'BBQ master', 'Vegetarian cooking', 'Pastry chef', 'Home brewer'];

const TIMEZONES = [
  { group:'North America', zones:[
    { value:'America/St_Johns',    label:'Newfoundland (NST/NDT)' },
    { value:'America/Halifax',     label:'Atlantic (AST/ADT)' },
    { value:'America/Toronto',     label:'Eastern (EST/EDT)' },
    { value:'America/Chicago',     label:'Central (CST/CDT)' },
    { value:'America/Denver',      label:'Mountain (MST/MDT)' },
    { value:'America/Vancouver',   label:'Pacific (PST/PDT)' },
    { value:'America/Anchorage',   label:'Alaska (AKST/AKDT)' },
    { value:'Pacific/Honolulu',    label:'Hawaii (HST)' },
  ]},
  { group:'Europe', zones:[
    { value:'Europe/London',       label:'London (GMT/BST)' },
    { value:'Europe/Paris',        label:'Paris / Berlin (CET/CEST)' },
    { value:'Europe/Helsinki',     label:'Helsinki (EET/EEST)' },
    { value:'Europe/Moscow',       label:'Moscow (MSK)' },
  ]},
  { group:'Asia / Pacific', zones:[
    { value:'Asia/Dubai',          label:'Dubai (GST)' },
    { value:'Asia/Kolkata',        label:'India (IST)' },
    { value:'Asia/Singapore',      label:'Singapore (SGT)' },
    { value:'Asia/Tokyo',          label:'Tokyo (JST)' },
    { value:'Australia/Sydney',    label:'Sydney (AEST/AEDT)' },
    { value:'Pacific/Auckland',    label:'Auckland (NZST/NZDT)' },
  ]},
  { group:'Other', zones:[
    { value:'UTC',                 label:'UTC' },
    { value:'America/Sao_Paulo',   label:'São Paulo (BRT)' },
    { value:'Africa/Johannesburg', label:'Johannesburg (SAST)' },
  ]},
];

const inputStyle = {
  width:'100%', padding:'10px 13px', border:'1px solid #E5D9C8',
  borderRadius:10, fontFamily:'Jost,sans-serif', fontSize:14,
  color:'#2C1A0E', outline:'none', marginBottom:16, background:'white',
};

const AVATAR_COLORS = ['#C4612A','#5D4037','#1A237E','#1B5E20','#BF360C','#4A148C'];

export default function ProfileView() {
  const { profile, updateProfile, user } = useAuth();

  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    timezone:  profile?.timezone  || 'America/Denver',
    dietary:   profile?.dietary   || [],
    skills:    profile?.skills    || [],
  });

  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState('');
  const [saveErr, setSaveErr]     = useState('');

  const [pwForm, setPwForm]       = useState({ password:'', confirm:'' });
  const [pwSaving, setPwSaving]   = useState(false);
  const [pwMsg, setPwMsg]         = useState('');
  const [pwErr, setPwErr]         = useState('');

  const initials = form.full_name.split(' ').map(w=>w[0]).join('').toUpperCase() || '?';
  const avatarBg = AVATAR_COLORS[(user?.id||'').charCodeAt(0) % AVATAR_COLORS.length];

  function toggleItem(field, val) {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(val)
        ? f[field].filter(x => x !== val)
        : [...f[field], val]
    }));
  }

  async function handleSaveProfile() {
    setSaving(true); setSaveMsg(''); setSaveErr('');
    try {
      await updateProfile(form);
      setSaveMsg('Profile saved successfully!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch(e) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword() {
    if (!pwForm.password) return setPwErr('Please enter a new password');
    if (pwForm.password.length < 6) return setPwErr('Password must be at least 6 characters');
    if (pwForm.password !== pwForm.confirm) return setPwErr('Passwords do not match');
    setPwSaving(true); setPwMsg(''); setPwErr('');
    try {
      const { error } = await supabase.auth.updateUser({ password: pwForm.password });
      if (error) throw error;
      setPwMsg('Password changed successfully!');
      setPwForm({ password:'', confirm:'' });
      setTimeout(() => setPwMsg(''), 3000);
    } catch(e) {
      setPwErr(e.message);
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div>
      <div style={{ ...S.pageHero }}>
        <h2 style={S.h2}>Your Profile</h2>
        <p style={S.textMuted}>Manage your personal details, dietary needs and cooking skills</p>
      </div>

      <div style={{ ...S.content, maxWidth:640 }}>

        {/* Avatar & name */}
        <div style={{ ...S.card, ...S.cardPad, marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:24 }}>
            <div style={{ width:64, height:64, borderRadius:'50%', background:avatarBg, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:24, fontWeight:500, flexShrink:0 }}>
              {initials}
            </div>
            <div>
              <p style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22 }}>{form.full_name || 'Your name'}</p>
              <p style={{ fontSize:13, color:'#7A6555' }}>{user?.email}</p>
            </div>
          </div>

          <label style={S.label}>Full name</label>
          <input style={inputStyle} placeholder="Your full name"
            value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name:e.target.value }))} />

          <label style={S.label}>Timezone</label>
          <select style={inputStyle} value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone:e.target.value }))}>
            {TIMEZONES.map(group => (
              <optgroup key={group.group} label={group.group}>
                {group.zones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </optgroup>
            ))}
          </select>

          {saveErr && <div style={{ background:'#FFEBEE', color:'#B71C1C', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 }}>{saveErr}</div>}
          {saveMsg && <div style={{ background:'#E8F5E9', color:'#1B5E20', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 }}>{saveMsg}</div>}

          <button style={{ ...S.btnPrimary, opacity:saving?0.5:1 }} disabled={saving} onClick={handleSaveProfile}>
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>

        {/* Dietary preferences */}
        <div style={{ ...S.card, ...S.cardPad, marginBottom:20 }}>
          <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, marginBottom:4 }}>Dietary preferences</h3>
          <p style={{ fontSize:13, color:'#7A6555', marginBottom:16 }}>Hosts can see these when planning the menu</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
            {DIETARY_OPTIONS.map(d => {
              const selected = form.dietary.includes(d);
              return (
                <button key={d} onClick={() => toggleItem('dietary', d)}
                  style={{ padding:'7px 14px', borderRadius:20, fontSize:13, cursor:'pointer', fontFamily:'Jost,sans-serif', fontWeight:500, border:selected?'2px solid #C4612A':'2px solid #E5D9C8', background:selected?'#FAEEE8':'white', color:selected?'#7B2D0D':'#7A6555', transition:'all .15s' }}>
                  {d}
                </button>
              );
            })}
          </div>
          <button style={{ ...S.btnPrimary, opacity:saving?0.5:1 }} disabled={saving} onClick={handleSaveProfile}>
            {saving ? 'Saving…' : 'Save preferences'}
          </button>
        </div>

        {/* Cooking skills */}
        <div style={{ ...S.card, ...S.cardPad, marginBottom:20 }}>
          <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, marginBottom:4 }}>Cooking skills</h3>
          <p style={{ fontSize:13, color:'#7A6555', marginBottom:16 }}>Helps hosts know what you're good at</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
            {SKILL_OPTIONS.map(s => {
              const selected = form.skills.includes(s);
              return (
                <button key={s} onClick={() => toggleItem('skills', s)}
                  style={{ padding:'7px 14px', borderRadius:20, fontSize:13, cursor:'pointer', fontFamily:'Jost,sans-serif', fontWeight:500, border:selected?'2px solid #5B7A5B':'2px solid #E5D9C8', background:selected?'#EBF2EB':'white', color:selected?'#2e4a2e':'#7A6555', transition:'all .15s' }}>
                  {s}
                </button>
              );
            })}
          </div>
          <button style={{ ...S.btnPrimary, opacity:saving?0.5:1 }} disabled={saving} onClick={handleSaveProfile}>
            {saving ? 'Saving…' : 'Save skills'}
          </button>
        </div>

        {/* Change password */}
        <div style={{ ...S.card, ...S.cardPad, marginBottom:20 }}>
          <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, marginBottom:4 }}>Change password</h3>
          <p style={{ fontSize:13, color:'#7A6555', marginBottom:16 }}>Set a new password for your account</p>

          <label style={S.label}>New password</label>
          <input type="password" style={inputStyle} placeholder="At least 6 characters"
            value={pwForm.password} onChange={e => setPwForm(f => ({ ...f, password:e.target.value }))} />

          <label style={S.label}>Confirm new password</label>
          <input type="password" style={inputStyle} placeholder="Repeat your new password"
            value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm:e.target.value }))} />

          {pwErr && <div style={{ background:'#FFEBEE', color:'#B71C1C', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 }}>{pwErr}</div>}
          {pwMsg && <div style={{ background:'#E8F5E9', color:'#1B5E20', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 }}>{pwMsg}</div>}

          <button style={{ ...S.btnPrimary, opacity:pwSaving?0.5:1 }} disabled={pwSaving} onClick={handleChangePassword}>
            {pwSaving ? 'Changing…' : 'Change password'}
          </button>
        </div>

      </div>
    </div>
  );
}
